import {
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';

import { GasInputBoolean, NgOnInit } from '@cikrf/gas-utils/decorators';

import { GasForm } from './gas-form.namespace';
import { delay, distinctUntilChanged, pairwise, startWith } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
export abstract class GasFormBaseComponent<T = GasForm.GasFilterFormBaseValue> implements ControlValueAccessor, OnChanges, OnInit {

  @Input()
  public value: T;

  @Input()
  public set state(value: GasForm.InputState | string) {
    this.innerStateSubject$.next(value || GasForm.InputState.Default);

    this.typeChanged$.emit(value);
  }

  public get state(): GasForm.InputState | string {
    return this.innerStateSubject$.getValue();
  }

  /** Состояние не активности */
  @GasInputBoolean()
  @Input()
  @HostBinding('class._disabled')
  public set nativeDisabled(isDisabled: string | boolean) {
    if (typeof isDisabled === 'string') {
      isDisabled = isDisabled === '' ? true : false;
    }
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  public get nativeDisabled(): string | boolean {
    return this.form.disabled;
  }

  @GasInputBoolean()
  @Input()
  @HostBinding('class._required')
  public set required(isRequired: string | boolean) {
    if (typeof isRequired === 'string') {
      isRequired = isRequired === '' ? true : false;
    }

    this.requiredSubject$.next(isRequired);
  }

  public get required(): string | boolean {
    return this.requiredSubject$.getValue();
  }

  @Input()
  public combine: string;

  @Input()
  public combinePos: 'first' | 'last';

  @Output('onChanged')
  public onChanged$ = new EventEmitter<GasForm.GasFilterFormBaseValue>();

  @NgOnInit()
  public init$!: Observable<void>;

  public typeChanged$ = new EventEmitter<GasForm.InputState | string>();

  public get classDefault(): boolean {
    return !this.state || this.state === GasForm.InputState.Default;
  }

  public get classPrimary(): boolean {
    return this.state === GasForm.InputState.Primary;
  }

  // todo: надо уйти от создания формы тут и получать её из ValueAccessor
  public form = new FormControl();

  public required$: Observable<boolean>;

  private requiredSubject$ = new BehaviorSubject<boolean>(false);

  private innerStateSubject$ = new BehaviorSubject<GasForm.InputState | string>(null);

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
  ) {
    this.required$ = this.requiredSubject$.asObservable();

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
      )
      .subscribe((value: GasForm.GasFilterFormBaseValue) => {
        this.onChanged$.emit(value);

        this.onChange(value);
      });

    this.innerStateSubject$.asObservable()
      .pipe(
        untilDestroyed(this),
        startWith(GasForm.InputState.Default),
        pairwise(),
        delay(50),
      )
      .subscribe(([oldState, newState]: [GasForm.InputState, GasForm.InputState]) => {
        const element: HTMLElement = this.elementRef.nativeElement;

        if (oldState) {
          this.renderer.removeClass(element, `_${oldState}`);
        }

        const stateClass = `_${newState || GasForm.InputState.Default}`;

        this.renderer.addClass(element, stateClass);

        if (!this.changeDetectorRef['destroyed']) {
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  public ngOnInit(): void {
    if (!this.state) {
      this.state = GasForm.InputState.Default;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value?.currentValue) {
      this.form.setValue(this.value);
    }
  }

  public writeValue(value: GasForm.GasFilterFormBaseValue): void {
    this.form.setValue(value);

    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
    }
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  protected onChange = (value: GasForm.GasFilterFormBaseValue) => { };
  protected onTouched = () => { };
}
